import { useCallback } from 'react';
import { useEnhancedSalesOrderUi } from '@/api/org/config/useEnhancedSalesOrderUi';
import { useGT } from 'gt-react';
import type { AppRouteMeta } from '@/routing/route-meta';
import { useRouteMessage } from '@/routing/route-messages';

// TEMPORARY — remove after all tenants migrate to OMS v3 and the legacy pages
// are deleted.
//
// The legacy pages moved to versioned paths (/sales-orders-v2, /discounts-v1)
// but intentionally keep the same nav labels as the promoted OMS v3 pages
// ("Sales Orders", "Discounts") so the tenants still on them see no change.
// An org on the new experience can have both variants visible at once (until
// the legacy RBAC grants are removed from its teams), so its legacy items get
// a "(Legacy)" suffix to tell the duplicates apart.
const LEGACY_OMS_ROUTE_IDS = new Set([
  '/authenticated/sales-orders-v2',
  '/authenticated/discounts-v1',
]);

type NavEntryLike = {
  id: string;
  fullPath: string;
  meta: AppRouteMeta;
};

/**
 * Display label for a nav entry: the route's declared `nav.label` (falling
 * back to its path), with a temporary "(Legacy)" suffix on the moved legacy
 * OMS pages when the org uses the new OMS experience — see above.
 *
 * Labels are declared as `msg(...)` in route metadata, so this is where they get
 * decoded and translated; a path fallback, or a label a new route ships raw,
 * passes through untouched.
 */
export function useNavEntryLabel(): (entry: NavEntryLike) => string {
  const { isEnhancedSalesOrderUiEnabled } = useEnhancedSalesOrderUi();
  const routeMessage = useRouteMessage();
  const gt = useGT();

  return useCallback(
    (entry: NavEntryLike) => {
      const label = routeMessage(entry.meta.nav?.label ?? entry.fullPath);
      return isEnhancedSalesOrderUiEnabled && LEGACY_OMS_ROUTE_IDS.has(entry.id)
        ? gt('{label} (Legacy)', {
            context:
              'sidebar nav label for a superseded page, kept visible alongside its replacement',
            label,
          })
        : label;
    },
    [gt, isEnhancedSalesOrderUiEnabled, routeMessage],
  );
}
